import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, ChevronRight, Phone, Wrench, ShoppingBag, RefreshCw, Search, X, ArrowRight, CheckCircle2, Circle, Edit2, Check, Navigation, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';
import { formatAddress, formatFullAddress } from '../utils/addressFormatter';

export default function FieldMode() {
  const [screen, setScreen] = useState('city'); // 'city' | 'list' | 'detail'
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null); // null = all
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [activityHistory, setActivityHistory] = useState(null); // null = not loaded yet
  const [activityExpanded, setActivityExpanded] = useState(false);

  const loadCities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/field-mode/cities');
      setCities(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCities(); }, [loadCities]);

  const loadCustomers = useCallback(async (city) => {
    setLoading(true);
    setSearch('');
    try {
      const params = city ? { city } : {};
      const res = await api.get('/field-mode/customers', { params });
      setCustomers(res.data);
      setFilteredCustomers(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  function selectCity(city) {
    setSelectedCity(city);
    loadCustomers(city);
    setScreen('list');
  }

  function refresh() {
    if (screen === 'city') loadCities();
    if (screen === 'list') loadCustomers(selectedCity);
  }

  // Client-side search filter
  useEffect(() => {
    if (!search.trim()) {
      setFilteredCustomers(customers);
    } else {
      const q = search.toLowerCase();
      setFilteredCustomers(customers.filter((c) =>
        c.name?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.phones?.some((p) => p.phone.includes(search))
      ));
    }
  }, [search, customers]);

  async function closeServiceCall(scId, customerId) {
    try {
      await api.put(`/service-calls/${scId}`, { status: 'closed' });
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId
            ? { ...c, open_service_calls: c.open_service_calls.filter((sc) => sc.id !== scId) }
            : c
        )
      );
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer((c) => ({
          ...c,
          open_service_calls: c.open_service_calls.filter((sc) => sc.id !== scId),
        }));
      }
    } catch { alert('שגיאה בעדכון קריאת שירות'); }
  }

  async function saveNote(scId, customerId) {
    try {
      await api.put(`/service-calls/${scId}`, { details: noteText || null });
      // Update locally
      const updateSc = (sc) => sc.id === scId ? { ...sc, details: noteText || null } : sc;
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId
            ? { ...c, open_service_calls: c.open_service_calls.map(updateSc) }
            : c
        )
      );
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer((c) => ({ ...c, open_service_calls: c.open_service_calls.map(updateSc) }));
      }
    } catch { alert('שגיאה בשמירת הערה'); }
    setEditingNoteId(null);
  }

  async function updateOrderStatus(orderId, customerId, status) {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      const updateOrder = (o) => {
        if (o.id !== orderId) return o;
        const updated = { ...o, status };
        // Remove only if completed AND paid
        return updated;
      };
      const filterOrUpdate = (orders) =>
        orders
          .map(updateOrder)
          .filter((o) => !(o.id === orderId && status === 'completed' && o.is_paid));
      setCustomers((prev) =>
        prev.map((c) => c.id === customerId ? { ...c, open_orders: filterOrUpdate(c.open_orders) } : c)
      );
      setSelectedCustomer((c) => c ? { ...c, open_orders: filterOrUpdate(c.open_orders) } : c);
    } catch { alert('שגיאה בעדכון סטטוס הזמנה'); }
  }

  async function toggleOrderPaid(orderId, customerId, currentIsPaid) {
    const is_paid = !currentIsPaid;
    try {
      await api.put(`/orders/${orderId}/paid`, { is_paid });
      const filterOrUpdate = (orders) =>
        orders
          .map((o) => o.id === orderId ? { ...o, is_paid } : o)
          .filter((o) => !(o.id === orderId && is_paid && o.status === 'completed'));
      setCustomers((prev) =>
        prev.map((c) => c.id === customerId ? { ...c, open_orders: filterOrUpdate(c.open_orders) } : c)
      );
      setSelectedCustomer((c) => c ? { ...c, open_orders: filterOrUpdate(c.open_orders) } : c);
    } catch { alert('שגיאה בעדכון תשלום'); }
  }

  function openDetail(customer) {
    setSelectedCustomer(customer);
    setScreen('detail');
    setActivityHistory(null);
    setActivityExpanded(false);
    // Load full activity history in background
    api.get(`/customers/${customer.id}`)
      .then((r) => setActivityHistory(r.data.activity || []))
      .catch(() => setActivityHistory([]));
  }

  const primaryPhone = (c) => c.phones?.find((p) => p.is_primary)?.phone || c.phones?.[0]?.phone;

  // ────────────────────────────────────────────
  // Screen 1: City Picker
  // ────────────────────────────────────────────
  if (screen === 'city') {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 p-4" dir="rtl">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <MapPin size={22} className="text-primary-600 dark:text-primary-400" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">מצב שטח</h1>
            </div>
            <button onClick={refresh} disabled={loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-2">
              {/* All cities option */}
              <button
                onClick={() => selectCity(null)}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <MapPin size={18} className="text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">כל הערים</p>
                    <p className="text-xs text-gray-400">
                      {cities.reduce((sum, c) => sum + parseInt(c.customer_count), 0)} לקוחות פעילים
                    </p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-gray-400" />
              </button>

              {cities.map((city) => (
                <button
                  key={city.city}
                  onClick={() => selectCity(city.city)}
                  className="w-full flex items-center justify-between p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      <MapPin size={18} className="text-gray-500" />
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">{city.city}</p>
                      <p className="text-xs text-gray-400">{city.customer_count} לקוחות</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-400" />
                </button>
              ))}

              {cities.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <MapPin size={40} className="mx-auto mb-3 opacity-40" />
                  <p>אין הזמנות פתוחות או קריאות שירות פתוחות</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────
  // Screen 2: Customer Tiles
  // ────────────────────────────────────────────
  if (screen === 'list') {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900" dir="rtl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button onClick={() => { setScreen('city'); loadCities(); }}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <ArrowRight size={18} />
              </button>
              <div>
                <h1 className="font-bold text-gray-900 dark:text-white text-base leading-tight">
                  {selectedCity || 'כל הערים'}
                </h1>
                <p className="text-xs text-gray-400">{filteredCustomers.length} לקוחות</p>
              </div>
            </div>
            <button onClick={refresh} disabled={loading}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
          {/* Search */}
          <div className="relative">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לקוח..."
              className="w-full pr-9 pl-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Customer grid */}
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShoppingBag size={40} className="mx-auto mb-3 opacity-40" />
              <p>אין לקוחות להצגה</p>
            </div>
          ) : (
            filteredCustomers.map((c) => (
              <button
                key={c.id}
                onClick={() => openDetail(c)}
                className="w-full text-right bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-400 hover:shadow-md transition-all shadow-sm"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{c.name || 'ללא שם'}</p>
                  <ChevronRight size={18} className="text-gray-400 shrink-0 mt-0.5" />
                </div>

                {primaryPhone(c) && (
                  <p className="text-base font-mono text-primary-600 dark:text-primary-400 mb-1" dir="ltr">
                    {primaryPhone(c)}
                  </p>
                )}

                {(c.city || formatAddress(c)) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {[c.city, formatAddress(c)].filter(Boolean).join(' · ')}
                  </p>
                )}

                <div className="flex gap-2 flex-wrap">
                  {c.open_orders.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      <ShoppingBag size={11} />
                      {c.open_orders.length} הזמנ{c.open_orders.length === 1 ? 'ה' : 'ות'} פתוח{c.open_orders.length === 1 ? 'ה' : 'ות'}
                    </span>
                  )}
                  {c.open_service_calls.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      <Wrench size={11} />
                      {c.open_service_calls.length} קריא{c.open_service_calls.length === 1 ? 'ה' : 'ות'} פתוח{c.open_service_calls.length === 1 ? 'ה' : 'ות'}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────
  // Screen 3: Customer Detail (full screen)
  // ────────────────────────────────────────────
  if (screen === 'detail' && selectedCustomer) {
    const c = selectedCustomer;
    return (
      <div className="min-h-full bg-white dark:bg-gray-900" dir="rtl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
          <button onClick={() => { setScreen('list'); loadCustomers(selectedCity); }}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <ArrowRight size={18} />
          </button>
          <h1 className="font-bold text-gray-900 dark:text-white truncate">{c.name || 'לקוח'}</h1>
        </div>

        <div className="p-5 space-y-6">
          {/* Customer info hero */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{c.name || 'ללא שם'}</h2>

            {/* Phones — large, tap to call */}
            {c.phones?.length > 0 && (
              <div className="space-y-2 mb-4">
                {c.phones.map((p) => (
                  <a
                    key={p.id}
                    href={`tel:${p.phone}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <Phone size={20} className="text-green-600 dark:text-green-400 shrink-0" />
                    <div>
                      <p className="text-2xl font-mono font-bold text-green-700 dark:text-green-300 leading-none" dir="ltr">
                        {p.phone}
                      </p>
                      {p.label && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">{p.label}</p>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}

            {/* Address — tap to open Waze */}
            {(c.city || formatAddress(c)) && (
              <a
                href={`https://maps.google.com/maps?daddr=${encodeURIComponent([c.city, c.street, c.street_number].filter(Boolean).join(' '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <MapPin size={18} className="text-gray-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  {c.city && <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">{c.city}</p>}
                  {(c.street || c.street_number || c.apartment) && (
                    <p className="text-base text-gray-600 dark:text-gray-400">
                      {[c.street, c.street_number, c.apartment ? `דירה ${c.apartment}` : null].filter(Boolean).join(' ')}
                    </p>
                  )}
                  {c.floor && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">קומה {c.floor}</p>
                  )}
                </div>
                <Navigation size={16} className="text-blue-500 shrink-0 mt-0.5" />
              </a>
            )}
          </div>

          {/* Open Orders */}
          {c.open_orders.length > 0 && (
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white mb-3">
                <ShoppingBag size={16} className="text-blue-500" />
                הזמנות פתוחות ({c.open_orders.length})
              </h3>
              <div className="space-y-3">
                {c.open_orders.map((o) => {
                  const items = Array.isArray(o.items) ? o.items : (o.items ? (() => { try { return JSON.parse(o.items); } catch { return []; } })() : []);
                  const completedUnpaid = o.status === 'completed' && !o.is_paid;
                  const computedTotal = items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.quantity) || 1), 0);
                  const discount = computedTotal > 0 && o.total_amount && computedTotal > Number(o.total_amount) + 0.01
                    ? computedTotal - Number(o.total_amount)
                    : 0;
                  return (
                    <div key={o.id} className={`rounded-xl border overflow-hidden ${
                      completedUnpaid
                        ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                        : 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20'
                    }`}>
                      {/* Order header */}
                      <div className={`flex items-center justify-between px-3 py-2 border-b ${
                        completedUnpaid ? 'border-red-200 dark:border-red-800' : 'border-blue-200 dark:border-blue-800'
                      }`}>
                        <span className={`font-semibold text-sm ${completedUnpaid ? 'text-red-800 dark:text-red-300' : 'text-blue-800 dark:text-blue-300'}`}>הזמנה #{o.order_number}</span>
                        {o.total_amount && (
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            ₪{Number(o.total_amount).toLocaleString()}
                          </span>
                        )}
                      </div>

                      {/* Items list */}
                      {items.length > 0 && (
                        <div className="px-3 py-2 space-y-1">
                          {items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="text-gray-700 dark:text-gray-300 flex-1 truncate">{item.description || item.name || ''}</span>
                              <span className="text-gray-500 dark:text-gray-400 mr-2 shrink-0">
                                {item.quantity && `×${item.quantity}`}
                                {item.price && ` ₪${Number(item.price).toLocaleString()}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {discount > 0 && (
                        <div className="px-3 pb-1 flex justify-between text-xs border-t border-blue-100 dark:border-blue-800/40 pt-1.5">
                          <span className="text-gray-400 dark:text-gray-500">סכום פריטים: ₪{computedTotal.toLocaleString()}</span>
                          <span className="text-red-500 dark:text-red-400">הנחה: -₪{discount.toLocaleString()}</span>
                        </div>
                      )}

                      {o.notes && (
                        <p className="px-3 pb-2 text-sm text-gray-500 dark:text-gray-400">{o.notes}</p>
                      )}

                      {/* Actions */}
                      <div className={`flex items-center gap-2 px-3 py-2 bg-white/60 dark:bg-gray-800/60 border-t ${
                        completedUnpaid ? 'border-red-200 dark:border-red-800' : 'border-blue-200 dark:border-blue-800'
                      }`}>
                        {/* Status select */}
                        <select
                          value={o.status}
                          onChange={(e) => updateOrderStatus(o.id, c.id, e.target.value)}
                          className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-blue-200 dark:border-blue-700
                                     bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value="new">חדשה</option>
                          <option value="sent">נשלחה</option>
                          <option value="completed">הסתיימה</option>
                        </select>

                        {/* Paid toggle */}
                        <button
                          onClick={() => toggleOrderPaid(o.id, c.id, o.is_paid)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors shrink-0 ${
                            o.is_paid
                              ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {o.is_paid
                            ? <><CheckCircle2 size={13} /> שולם</>
                            : <><Circle size={13} /> לא שולם</>
                          }
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Open Service Calls */}
          {c.open_service_calls.length > 0 && (
            <div>
              <h3 className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white mb-3">
                <Wrench size={16} className="text-amber-500" />
                קריאות שירות פתוחות ({c.open_service_calls.length})
              </h3>
              <div className="space-y-3">
                {c.open_service_calls.map((sc) => (
                  <div key={sc.id} className="p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                    <div className="flex items-center justify-between mb-2">
                      {sc.type_name && (
                        <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">{sc.type_name}</span>
                      )}
                      <button
                        onClick={() => closeServiceCall(sc.id, c.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                      >
                        <CheckCircle2 size={13} />
                        סגור
                      </button>
                    </div>

                    {/* Note / Details inline edit */}
                    {editingNoteId === sc.id ? (
                      <div className="mt-2">
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          rows={3}
                          autoFocus
                          className="w-full px-3 py-2 text-sm rounded-lg border border-amber-300 dark:border-amber-700
                                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => saveNote(sc.id, c.id)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium transition-colors">
                            <Check size={12} /> שמור
                          </button>
                          <button
                            onClick={() => setEditingNoteId(null)}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            ביטול
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-1">
                        {sc.details ? (
                          <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">{sc.details}</p>
                        ) : null}
                        <button
                          onClick={() => { setEditingNoteId(sc.id); setNoteText(sc.details || ''); }}
                          className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 hover:underline mt-1"
                        >
                          <Edit2 size={11} />
                          {sc.details ? 'ערוך הערה' : 'הוסף הערה'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {c.open_orders.length === 0 && c.open_service_calls.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <p>אין פריטים פתוחים</p>
            </div>
          )}

          {/* Activity History — collapsible */}
          {(() => {
            const activityTypeLabel = {
              call: '📞 שיחה',
              order_created: '🛍️ הזמנה נפתחה',
              order_status: '📦 עדכון הזמנה',
              status_changed: '🔄 שינוי סטטוס',
              customer_created: '👤 לקוח נוצר',
              customer_updated: '✏️ פרטים עודכנו',
              document_created: '📄 מסמך נוצר',
              note: '📝 הערה',
            };
            return (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setActivityExpanded((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="flex items-center gap-2 font-semibold text-sm text-gray-700 dark:text-gray-300">
                    <Phone size={15} className="text-gray-500" />
                    היסטוריית התקשרויות
                    {activityHistory !== null && activityHistory.length > 0 && (
                      <span className="text-xs font-normal text-gray-400">({activityHistory.length})</span>
                    )}
                  </span>
                  {activityExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>

                {activityExpanded && (
                  <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
                    {activityHistory === null ? (
                      <div className="flex justify-center py-6">
                        <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
                      </div>
                    ) : activityHistory.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-6">אין פעילות</p>
                    ) : (
                      activityHistory.map((a) => (
                        <div key={a.id} className="px-4 py-3 flex items-start gap-3">
                          <span className="text-base mt-0.5 shrink-0">
                            {activityTypeLabel[a.type]?.split(' ')[0] || '•'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 dark:text-white">{a.description}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {new Date(a.created_at).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                              {' '}
                              {new Date(a.created_at).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
                              {a.performed_by_name && ` · ${a.performed_by_name}`}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  return null;
}
